// eslint-disable-next-line
import {
	getRouters
} from '@/api/menu'
import {
	indexRouterMap
} from '@/config/router.config'
import allIcon from '@/core/icons'
import {
	validURL
} from '@/utils/validate'
import {
	UserLayout,
	BlankLayout,
	PageView
} from '@/layouts'
import auth from '@/plugins/auth'
import store from '@/store'
import { consumeSwitchAccountRedirect } from '@/utils/switchAccount'
import {
	FALLBACK_ROUTE_PATH,
	isPathInRoutes,
	resolveFallbackRoutePath
} from '@/utils/routeAccess'
// 前端路由表
const constantRouterComponents = {
	// 基础页面 layout 必须引入
	BasicLayout: () => import('@/layouts/BasicLayout'),
	BlankLayout: BlankLayout,
	RouteView: () => import('@/layouts/RouteView'),
	PageView: PageView,
	UserLayout: UserLayout, // 登陆注册页面的通用布局

	// account
	'AccountCenter': () => import('@/views/account/center'),
	'AccountSettings': () => import('@/views/account/settings'),
	// 授权用户
	'AuthUser': () => import('@/views/system/role/authUser'),
	// 公告新增修改
	'NoticeForm': () => import('@/views/system/notice/modules/CreateForm'),
	//
	'zrIndex': () => import('@/views/sms/channel/zrdata'),
	// 修改生成配置
	'GenEdit': () => import('@/views/tool/gen/modules/GenEdit'),
	//客户信息
	'CustomerDetails': () => import('@/views/customer/CustomerReview/CustomerDetails'),
	// 新增喜报
	'AddGoodNews': () => import('@/views/system/AddGoodNews/index'),
	'GoodNewsPage': () => import('@/views/system/goodNewsSet/index'),
	'HelpDoc': () => import('@/views/system/HelpDoc/index'),
	'newList': () => import('@/views/system/notice/newList'),
	'newlyAdded': () => import('@/views/personnel/administration/newlyAdded'),

}

// 前端未找到页面路由（固定不用改）
const notFoundRouter = {
	path: '*',
	redirect: '/404',
	hidden: true
}

// 根级菜单
const rootMenu = {
	key: '',
	name: 'index',
	path: '/',
	component: 'BasicLayout',
	redirect: '',
	meta: {
		title: ''
	},
	children: []
}

// 根级路由
const rootRouter = {
	key: '',
	name: 'index',
	path: '/',
	component: constantRouterComponents.BasicLayout,
	redirect: '',
	meta: {
		title: ''
	},
	children: []
}

// 是否为首页菜单
function isHomeMenu(item) {
	if (!item) return false
	const title = item.meta && item.meta.title
	return item.path === '/index' ||
		item.path === 'index' ||
		item.name === 'Index' ||
		item.component === 'Index' ||
		title === '首页'
}

// 过滤首页菜单
function filterHomeMenu(menuList = []) {
	return menuList.filter(item => {
		if (isHomeMenu(item)) {
			return false
		}
		if (item.children && item.children.length) {
			item.children = filterHomeMenu(item.children)
		}
		return true
	})
}

function isLayoutComponent(component) {
	return !component || component === 'Layout' || component === 'ParentView'
}

// 子级 path 拼接父级 path（子 path 已含父级前缀时不重复拼接）
function joinMenuPath(parentPath = '', segment = '') {
	const seg = String(segment || '').replace(/^\//, '')
	if (!seg) {
		return parentPath || '/'
	}
	if (validURL(seg)) {
		return seg
	}
	const parent = String(parentPath || '').replace(/\/+$/, '')
	if (!parent || parent === '/') {
		return `/${seg}`.replace(/\/+/g, '/')
	}
	const parentNoSlash = parent.replace(/^\//, '')
	if (seg === parentNoSlash || seg.startsWith(`${parentNoSlash}/`)) {
		return `/${seg}`.replace(/\/+/g, '/')
	}
	return `/${parentNoSlash}/${seg}`.replace(/\/+/g, '/')
}

// 从 getRouters 取第一个可访问页面 path（按菜单顺序，逐级拼接 path）
function getFirstMenuPathFromApi(menuList = [], parentPath = '') {
	for (const item of menuList) {
		if (isHomeMenu(item) || item.hidden) {
			continue
		}
		if (item.path === '/' && item.children && item.children.length === 1) {
			return getFirstMenuPathFromApi(item.children, parentPath)
		}
		const currentPath = joinMenuPath(parentPath, item.path || '')
		if (!isLayoutComponent(item.component)) {
			return currentPath
		}
		if (item.children && item.children.length) {
			const childPath = getFirstMenuPathFromApi(item.children, currentPath)
			if (childPath) {
				return childPath
			}
		}
	}
	return null
}

// 取第一个可访问菜单路由（生成后的扁平路由兜底）
function getFirstRouterPath(routers = []) {
	const target = routers.find(route => route.path && route.path !== '/index' && route.path !== '/' && !route.hidden && route.name !== 'Index')
	return target ? target.path : null
}

function resolveDefaultRoutePath(menuList, routers) {
	let path = getFirstMenuPathFromApi(menuList) || getFirstRouterPath(routers)
	if (path && !isPathInRoutes(path, routers)) {
		path = null
	}
	if (!path || path === '/404') {
		path = resolveFallbackRoutePath(routers, FALLBACK_ROUTE_PATH)
	}
	return path || '/404'
}

// 仅一个可见一级菜单时，侧栏不展示该一级，直接展示其子菜单（路由结构不变）
function adjustSidebarMenusForSingleTop(menus, menuList = []) {
	const visibleTopMenus = menuList.filter(item => !item.hidden && !isHomeMenu(item))
	if (visibleTopMenus.length !== 1) {
		return menus
	}
	const root = menus.find(item => item.path === '/')
	if (!root || !root.children || !root.children.length) {
		return menus
	}
	const staticChildren = root.children.filter(item => item.hidden)
	const dynamicMenus = root.children.filter(item => !item.hidden)
	if (dynamicMenus.length !== 1) {
		return menus
	}
	const topMenuNode = dynamicMenus[0]
	if (!topMenuNode.children || !topMenuNode.children.length) {
		return menus
	}
	root.children = staticChildren.concat(topMenuNode.children)
	return menus
}

/**
 * 动态生成菜单
 * @param token
 * @returns {Promise<Router>}
 */
export const generatorDynamicRouter = (token) => {
	return new Promise((resolve, reject) => {
		// 向后端请求路由数据
		getRouters().then(res => {
			console.log('菜单管理', res)
			// 路由菜单分离，路由全部为二级，解决多级菜单缓存问题
			const menuList = filterHomeMenu(res.data || [])
			if (!menuList.length) {
				console.warn('getRouters 返回菜单为空，无法生成业务路由')
				const err = new Error('当前用户没有菜单权限')
				err.code = 'NO_MENU_PERMISSION'
				reject(err)
				return
			}
			const routers = []
			const menuNav = []
			const routerData = menuList
			const asyncRoutes = filterDynamicRoutes(indexRouterMap)
			rootMenu.children = asyncRoutes.concat(routerData)
			menuNav.push(rootMenu)
			const menus = generator(menuNav, null, routers)
			adjustSidebarMenusForSingleTop(menus, menuList)
			let defaultRoutePath = resolveDefaultRoutePath(menuList, routers)
			const switchRedirect = consumeSwitchAccountRedirect()
			if (switchRedirect) {
				defaultRoutePath = isPathInRoutes(switchRedirect, routers)
					? switchRedirect
					: resolveFallbackRoutePath(routers, FALLBACK_ROUTE_PATH)
			}
			if (!defaultRoutePath || defaultRoutePath === '/404') {
				defaultRoutePath = resolveFallbackRoutePath(routers, FALLBACK_ROUTE_PATH)
			}
			if (!defaultRoutePath || defaultRoutePath === '/404') {
				console.warn('未解析到可用首页路由，menuList/routers 可能异常', menuList, routers)
				const err = new Error('当前用户没有菜单权限')
				err.code = 'NO_MENU_PERMISSION'
				reject(err)
				return
			}
			rootMenu.redirect = defaultRoutePath
			rootRouter.redirect = defaultRoutePath
			rootRouter.children = routers.concat([
				{
					path: '/index',
					redirect: defaultRoutePath,
					hidden: true
				}
			])
			menus.push(notFoundRouter)
			let buttonPermissionIds = getAllButtonIds(menuList)
			console.log(buttonPermissionIds)
			store.dispatch('menu/saveMenuData', {
				menuList: menuList, // 后端原始菜单
				menus: menus, // 生成好的导航菜单

				routers: rootRouter,// 最终路由
				buttonPermissionIds: buttonPermissionIds,
				routers: rootRouter ,// 最终路由
				buttonPermissionIds: buttonPermissionIds,
				defaultRoutePath: defaultRoutePath

			})
			resolve({
				menus,
				routers: rootRouter
			})
		}).catch(err => {
			reject(err)
		})
	})
}

// 修复按钮权限方法 —— 100% 不报错版本
function getAllButtonIds(menuList) {
	let buttonIds = []
	// 递归遍历菜单
	function traverse(menus) {
		if (!menus || !menus.length) return
		menus.forEach(item => {
			// 如果有 buttons，就把 id 收集起来
			if (item.buttons && item.buttons.length) {
				item.buttons.forEach(btn => {
					if (btn && btn.id !== undefined && btn.id !== null && btn.id !== '') {
						buttonIds.push(String(btn.id))
					}
				})
			}
			// 继续遍历子菜单
			if (item.children && item.children.length) {
				traverse(item.children)
			}
		})
	}
	traverse(menuList)
	// 去重并返回
	return [...new Set(buttonIds)]
}


// 动态路由遍历，验证是否具备权限
export function filterDynamicRoutes(routes) {
	const res = []
	routes.forEach(route => {
		if (route.permissions) {
			if (auth.hasPermiOr(route.permissions)) {
				res.push(route)
			}
		} else if (route.roles) {
			if (auth.hasRoleOr(route.roles)) {
				res.push(route)
			}
		} else {
			res.push(route)
		}
	})
	return res
}

/**
 * 格式化树形结构数据 生成 vue-router 层级路由表
 *
 * @param routerMap
 * @param parent
 * @param routers
 * @returns {*}
 */
export const generator = (routerMap, parent, routers) => {
	const names = parent ? parent.meta.names : []
	return routerMap.map(item => {
		// 适配ruoyi一级菜单
		if (item.path === '/' && item.children && item.children.length === 1) {
			item = item.children[0]
			item.children = undefined
		}

		const {
			title,
			show,
			hideChildren,
			hiddenHeaderContent,
			hidden,
			icon,
			noCache
		} = item.meta || {}
		if (item.isFrame === 0) {
			item.target = '_blank'
		}
		const name = item.name || item.key || ''
		const isRouter = item.component && item.component !== 'Layout' && item.component !== 'ParentView'
		const currentRouter = {
			// 如果路由设置了 path，则作为默认 path，否则 路由地址 动态拼接生成如 /dashboard/workplace
			path: item.path || `${parent && parent.path || ''}/${item.path}`,
			// 路由名称，建议唯一
			name: name,
			// 该路由对应页面的 组件(动态加载)
			component: (constantRouterComponents[item.component || item.key]) || (() => import(
				`@/views/${item.component}`).then(comp => {
				// 与路由 name 对齐，保证 keep-alive include 命中，菜单来回切换走缓存而非整页重建
				if (comp.default && name) {
					comp.default.name = name
				}
				return comp
			})),
			hidden: item.hidden,
			hasExplanation: item.hasExplanation,
			hasExplanationVideo: item.hasExplanationVideo,
			menuId: item.id,
			// meta: 页面标题, 菜单图标, 页面权限(供指令权限用，可去掉)
			meta: {
				title: title,
				// icon: allIcon[icon + 'Icon'] || icon, 2025 取消所有图标显示
				hiddenHeaderContent: hiddenHeaderContent,
				// 目前只能通过判断path的http链接来判断是否外链，适配若依
				target: validURL(item.path) ? '_blank' : '',
				permission: item.name,
				keepAlive: noCache === undefined ? false : !noCache,
				hidden: hidden,
				// 因菜单路由分离，通过此names确定菜单树的展开
				names: names.concat([name])
			},
			redirect: item.redirect
		}
		// 是否设置了隐藏菜单
		if (show === false) {
			currentRouter.hidden = true
		}
		// 适配若依：子级 path 前拼父级 path，避免重复拼接
		const parentPath = parent && parent.path ? parent.path : ''
		currentRouter.path = joinMenuPath(parentPath, item.path || '')
		// 是否设置了隐藏子菜单
		if (hideChildren) {
			currentRouter.hideChildrenInMenu = true
		}
		// const names = parent.names
		const router = {
			path: currentRouter.path,
			// 路由名称，建议唯一
			name: currentRouter.name,
			// 该路由对应页面的 组件(动态加载)
			component: currentRouter.component,
			hidden: currentRouter.hidden,
			// meta: 页面标题, 菜单图标, 页面权限(供指令权限用，可去掉)
			meta: currentRouter.meta,
			redirect: currentRouter.redirect
		}
		if (router.component && isRouter) {
			routers.push(router)
		}
		// 是否有子菜单，并递归处理，并将父path传入
		if (item.children && item.children.length > 0) {
			// Recursion
			currentRouter.children = generator(item.children, currentRouter, routers)
		}
		return currentRouter
	})
}